/**
 * Copy plain text to the clipboard from the renderer (Electron + web).
 *
 * Separate from image-clipboard.js because text needs a fallback that images do
 * not: the async Clipboard API only reliably works while the page still holds
 * "transient user activation". A copy that happens AFTER an await — fetching a
 * rendered message from the server, say — can land outside that window and be
 * rejected, so this falls back to the legacy execCommand path, which has no such
 * requirement.
 *
 * Electron grants `clipboard-sanitized-write` for the async API (see src/main.js),
 * so the fast path is the normal one there.
 *
 * @param {string} text
 * @returns {Promise<void>} resolves on success, rejects when no path worked.
 */
export async function copyTextToClipboard(text) {
  const value = String(text ?? "");
  if (!value) {
    throw new Error("Nothing to copy");
  }

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return;
    } catch {
      // Fall through — most often a lost user activation, which execCommand survives.
    }
  }

  if (typeof document === "undefined" || !document.body) {
    throw new Error("Copy is not available here");
  }

  // Off-screen rather than hidden: execCommand("copy") ignores a textarea that is
  // display:none or visibility:hidden, and readOnly stops mobile keyboards popping up.
  const scratch = document.createElement("textarea");
  scratch.value = value;
  scratch.setAttribute("readonly", "");
  scratch.setAttribute("aria-hidden", "true");
  scratch.style.position = "fixed";
  scratch.style.top = "-9999px";
  scratch.style.opacity = "0";
  document.body.appendChild(scratch);

  const selection = document.getSelection();
  // Preserve whatever the user had selected — copying must not steal their selection.
  const previousRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

  try {
    scratch.select();
    scratch.setSelectionRange(0, value.length);
    if (!document.execCommand("copy")) {
      throw new Error("Copy was blocked");
    }
  } finally {
    scratch.remove();
    if (previousRange && selection) {
      selection.removeAllRanges();
      selection.addRange(previousRange);
    }
  }
}
